import React, { useState } from "react";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@material-ui/core";
import psl from 'psl';
import { FieldValidationError, ValidationHelper } from "../../services/server/ServerResponses";
import { useIsMobile } from "../Mui3WidthProvider";
import { ShareService } from "../../services/ShareService";
import { ShareItem } from "../../entities/ShareItem";
import UserSharePreference from "../../entities/UserSharePreference";

interface Props {
    isOpen: boolean;
    itemId: string;
    onClose: () => void;
    handleShareSuccess: (item:ShareItem, preferences:UserSharePreference) => void;
}

const parseUrl = (unsafeUrl:string) =>{
    if (unsafeUrl.startsWith('https')) {
        unsafeUrl = unsafeUrl.replace('https', '');
    } else if (unsafeUrl.startsWith('http')) {
        unsafeUrl = unsafeUrl.replace('http', '');
    }

    unsafeUrl = unsafeUrl.replace(/\b(www)\b/g, '');
    unsafeUrl = unsafeUrl.replace('.com', '');
    unsafeUrl = unsafeUrl.replace('.net', '');
    unsafeUrl = unsafeUrl.replace('.org', '');
    unsafeUrl = unsafeUrl.replace('.church', '');

    unsafeUrl = unsafeUrl.replace(/\s/g, "-");
    unsafeUrl = unsafeUrl.replace(/-{2,}/g, '-');
    unsafeUrl = unsafeUrl.replace(/_{2,}/g, '_');
    unsafeUrl = unsafeUrl.replace(/^-+|-+$/g, '');
    unsafeUrl = unsafeUrl.replace(/^_+|_+$/g, '');
    unsafeUrl = unsafeUrl.replace(/[^a-zA-Z0-9-_]+/g, '').toLowerCase();
    return unsafeUrl;
} 


export function SubdomainSetupDialog(props:Props){
    const [disabled, setDisabled] = useState(false);
    const [noChurchSite, setNoChurchSite] = useState(false);
    const [confirmDomainSet, setConfirmDomainSet] = useState(false);
    const [churchWebsiteForDomain, setChurchWebsiteForDomain] = useState("");
    const [parsedChurchWebsiteForDomain, setParsedChurchWebsiteForDomain] = useState("");
    const [churchNameForDomain, setChurchNameForDomain] = useState("");
    const [parsedChurchNameForDomain, setParsedChurchNameForDomain] = useState("");
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const isMobile = useIsMobile();

    const validateAndSetDomainFromChurchWebsite = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChurchWebsiteForDomain(e.currentTarget.value);
        let unsafeUrl = e.currentTarget.value;

        if (psl.isValid(unsafeUrl)) {
            var parsed: any = psl.parse(unsafeUrl);
            unsafeUrl = parsed.sld;
        }

        let encoded = encodeURI(parseUrl(unsafeUrl));

        let errors: FieldValidationError[] = [];
        if (encoded.length > 20) {
            const lengthError: FieldValidationError = { field: "Subdomain", errors: ["Oops. The web address you entered is too long, please make it a bit shorter."] };
            errors = [lengthError];
            setErrors(errors)
        }

        setChurchWebsiteForDomain(encoded);
    }

   const validateAndSetDomainFromChurchName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChurchNameForDomain(e.currentTarget.value);
        const encoded = encodeURI(parseUrl(e.currentTarget.value));

        if (encoded.length > 20)
            setErrors([{ field: "Subdomain", errors: ["Oops. The name you entered is too long, please make it a bit shorter."] }]);

        setParsedChurchNameForDomain(encoded);
    }

    const handleNoWebsiteClick = () => {
        setErrors([]);
        setChurchNameForDomain("");
        setChurchWebsiteForDomain("");
        setParsedChurchNameForDomain("");
        setParsedChurchWebsiteForDomain("");
        setNoChurchSite(!noChurchSite);
    }

    const onSubmitDomain = async (e: React.FormEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if ((parsedChurchNameForDomain.length === 0 && parsedChurchWebsiteForDomain.length === 0) || disabled || !confirmDomainSet) {
            return;
        }

        setDisabled(true);
        const prefResponse = await ShareService.saveUserSharePreferences(noChurchSite ? parsedChurchNameForDomain : parsedChurchWebsiteForDomain);
        setDisabled(false);

        if(prefResponse.error) return setErrors([{ field: "", errors: [prefResponse.message] }]);
        if(prefResponse.validation) return setErrors(prefResponse.errors);

        setDisabled(true);
        const response = await ShareService.createUserShareItem(props.itemId)
        setDisabled(false);

        
        if(response.error) return setErrors([{ field: "", errors: [response.message] }]);
        if(response.data === null) return setErrors([{ field: "", errors: ["Unable to get share. Please try again"] }]);
        props.handleShareSuccess(response.data, prefResponse.data)
    }

    return (
        <Dialog
            open={props.isOpen}
            aria-labelledby="firsttime-form-dialog-title"
            disableBackdropClick={true}
        >
            <div style={{ minWidth: isMobile ? "initial" : 600}}>
                <form onSubmit={(e) => onSubmitDomain(e)}>
                    <DialogTitle id="url-form-dialog-title">Before you share your message...</DialogTitle>
                    <DialogContent>
                        <Typography
                            style={{ opacity: noChurchSite ? .6 : 1 }}
                            variant="subtitle1">
                            Enter the web address for your church:
                            </Typography>
                        <TextField
                            disabled={disabled || noChurchSite}
                            error={ValidationHelper.isFieldInError(errors, 'Subdomain') && !noChurchSite}
                            autoFocus
                            margin="dense"
                            id="urlDomain"
                            type="text"
                            style={{ minWidth: "400px", opacity: noChurchSite ? .6 : 1 }}
                            fullWidth
                            value={churchWebsiteForDomain}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => validateAndSetDomainFromChurchWebsite(e)}
                            helperText={ValidationHelper.isFieldInError(errors, 'Subdomain') && !noChurchSite && <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Subdomain') }} />}
                        />
                        <br />
                        <FormControlLabel
                            style={{ opacity: churchWebsiteForDomain.length > 0 ? .6 : 1 }}
                            control={
                                <Checkbox

                                    checked={noChurchSite}
                                    onChange={handleNoWebsiteClick}
                                    value="checkedB"
                                    color="default"
                                    disabled={disabled}
                                />
                            }
                            label="I do not have a church website"
                        />

                        {noChurchSite &&
                            <>
                                <br />
                                <br />
                                <Typography
                                    variant="subtitle1">
                                    Enter the name of your church or ministry
                                </Typography>
                                <TextField
                                    disabled={disabled}
                                    error={ValidationHelper.isFieldInError(errors, 'Subdomain') && noChurchSite}
                                    autoFocus
                                    margin="dense"
                                    id="urlDomain"
                                    type="text"
                                    style={{ minWidth: "400px" }}
                                    fullWidth
                                    value={churchNameForDomain}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => validateAndSetDomainFromChurchName(e)}
                                    helperText={ValidationHelper.isFieldInError(errors, 'Subdomain') && noChurchSite && <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Subdomain') }} />}
                                />
                            </>
                        }


                        <div style={{ marginTop: 16, height: 80 }}>
                            <Typography
                                variant="subtitle1"
                                style={{
                                    opacity: (parsedChurchNameForDomain.length > 0 || parsedChurchWebsiteForDomain.length > 0) ? 1 : .4
                                }}>
                                Your unique share URL:&nbsp;
                                        {parsedChurchWebsiteForDomain.length > 0 ? <strong>{parsedChurchWebsiteForDomain}.sermon.stream</strong> : <strong>{parsedChurchNameForDomain}.sermon.stream</strong>}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={confirmDomainSet}
                                        onChange={() => setConfirmDomainSet(!confirmDomainSet)}
                                        value="checkedB"
                                        color="primary"
                                        disabled={(parsedChurchWebsiteForDomain.length === 0 && parsedChurchNameForDomain.length === 0) || disabled}
                                    />
                                }
                                label="I understand that once saved this domain cannot be changed."
                            />
                        </div>

                    </DialogContent>
                    <DialogActions>
                        <Button disabled={disabled} onClick={() => props.onClose()} color="primary">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" disabled={disabled || !confirmDomainSet || (parsedChurchWebsiteForDomain.length === 0 && parsedChurchNameForDomain.length === 0)}>
                            Save
                       </Button>
                    </DialogActions>
                </form>
            </div>
        </Dialog>
    );
}